import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import {
    faEnvelopeOpenText,
    faHourglassStart,
    faPlay,
    faHourglassClock,
    faEdit,
} from "@fortawesome/pro-regular-svg-icons"
import { Button, Typography, Stack } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab"
import { StandardChip, StandardChipVariantType } from "../../../../../../shared/components/badges/StandardChip"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { SubscriptionDetailsInfo, SubscriptionDetailsStatus } from "../supplierMerchantsTypes"
import { EntityHistory } from "../../../../../support/admin/entities/supportEntitiesTypes"
import { BeginOnboardingDialog } from "./BeginOnboarding/BeginOnboardingDialog"
import { useDateTime } from "../../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    text: {
        color: theme.palette.text.secondary,
    },
    accentText: {
        color: theme.palette.text.primary,
    },
    chip: {
        fontWeight: 500,
    },
    timeline: {
        padding: 0,
        margin: 0,
        "& .MuiTimelineItem-root:last-child": {
            minHeight: "auto",
        },
        "& .MuiTimelineItem-root:before": {
            display: "none",
        },
    },
    lastDotDefault: {
        backgroundColor: "transparent",
        border: "2px solid #bdbdbd",
    },
    lastDotGreen: {
        backgroundColor: theme.palette.tailwind.green[400],
    },
    lastDotRed: {
        backgroundColor: theme.palette.tailwind.red[600],
    },
    button: {
        textTransform: "none",
        borderRadius: "0.5rem",
        border: "1px solid #2196F3",
        color: "#2196F3",
    },
}))

type EntitySubscriptionStatusProps = Readonly<{
    subscriptionDetails: SubscriptionDetailsInfo
    history: EntityHistory
}>

type TimelineItemType = {
    label: string
    date: string
}

function getChipVariant(status: SubscriptionDetailsStatus): StandardChipVariantType {
    switch (status) {
        case SubscriptionDetailsStatus.TRIAL:
            return "purple"
        case SubscriptionDetailsStatus.LIVE:
            return "success"
        case SubscriptionDetailsStatus.UNSUBSCRIBING:
            return "pink"
        case SubscriptionDetailsStatus.UNSUBSCRIBED:
            return "red"
        default:
            return "yellow"
    }
}

function getButtonIconVariant(status: SubscriptionDetailsStatus) {
    switch (status) {
        case SubscriptionDetailsStatus.CREATING:
            return faEnvelopeOpenText
        case SubscriptionDetailsStatus.ONBOARDING:
            return faHourglassStart
        case SubscriptionDetailsStatus.TRIAL:
            return faPlay
        case SubscriptionDetailsStatus.LIVE:
            return faHourglassClock
        default:
            return faEdit
    }
}

export function EntitySubscriptionStatus({ subscriptionDetails, history }: EntitySubscriptionStatusProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    if (!("status" in subscriptionDetails)) {
        return null
    }

    const [dialogState, setDialogState] = useState(false)
    const { status, implementationDeadline } = subscriptionDetails

    const implementationDeadlineValue = dateTimeFactory.formatDate(dateTimeFactory.fromApi(implementationDeadline))

    const timelineItems: TimelineItemType[] = []

    // Common for all statuses
    timelineItems.push({
        label: l10n.getString("support-create-supplier-subscription-status-created"),
        date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(history.created.timestamp)),
    })

    switch (status) {
        case SubscriptionDetailsStatus.ONBOARDING:
            timelineItems.push(
                {
                    label: l10n.getString("support-create-supplier-subscription-status-onboarding-email-sent"),
                    date: dateTimeFactory.formatDate(
                        dateTimeFactory.fromApi(subscriptionDetails.onboarding.documentsSent),
                    ),
                },
                {
                    label: l10n.getString("support-create-supplier-subscription-status-implementation-deadline"),
                    date: implementationDeadlineValue,
                },
            )
            break
        case SubscriptionDetailsStatus.TRIAL:
            timelineItems.push(
                {
                    label: l10n.getString("support-create-supplier-subscription-status-onboarding-email-sent"),
                    date: dateTimeFactory.formatDate(
                        dateTimeFactory.fromApi(subscriptionDetails.onboarding.documentsSent),
                    ),
                },
                {
                    label: l10n.getString("support-create-supplier-subscription-status-trial-started"),
                    date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.trial.start)),
                },
                {
                    label: l10n.getString("support-create-supplier-subscription-status-implementation-deadline"),
                    date: implementationDeadlineValue,
                },
            )
            break
        case SubscriptionDetailsStatus.LIVE:
            timelineItems.push({
                label: l10n.getString("support-create-supplier-subscription-status-subscription-started"),
                date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.subscription.start)),
            })
            break
        case SubscriptionDetailsStatus.UNSUBSCRIBING:
            timelineItems.push(
                {
                    label: l10n.getString("support-create-supplier-subscription-status-subscription-started"),
                    date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.subscription.start)),
                },
                {
                    label: l10n.getString("support-create-supplier-subscription-status-subscription-ending"),
                    date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.subscription.end!)),
                },
            )
            break
        case SubscriptionDetailsStatus.UNSUBSCRIBED:
            timelineItems.push(
                {
                    label: l10n.getString("support-create-supplier-subscription-status-subscription-started"),
                    date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.subscription.start)),
                },
                {
                    label: l10n.getString("support-create-supplier-subscription-status-subscription-ending"),
                    date: dateTimeFactory.formatDate(dateTimeFactory.fromApi(subscriptionDetails.subscription.end!)),
                },
            )
            break
        default:
            timelineItems.push({
                label: l10n.getString("support-create-supplier-subscription-status-implementation-deadline"),
                date: implementationDeadlineValue,
            })
            break
    }

    const lastDotStyle = (index: number) => {
        if (index === timelineItems.length - 1) {
            switch (status) {
                case SubscriptionDetailsStatus.LIVE:
                    return classes.lastDotGreen
                case SubscriptionDetailsStatus.UNSUBSCRIBED:
                    return classes.lastDotRed
                default:
                    return classes.lastDotDefault
            }
        }
        return classes.lastDotGreen
    }

    const renderDialog = () => {
        switch (status) {
            case SubscriptionDetailsStatus.CREATING:
                return <BeginOnboardingDialog open={dialogState} onClose={() => setDialogState(false)} />
            default:
                return null
        }
    }

    return (
        <Stack gap="2rem">
            <Stack flexDirection="row" justifyContent="space-between" gap="1rem">
                <LocalizedStrict id="support-create-supplier-subscription-status">
                    <Typography variant="body2" className={classes.title}>
                        Status
                    </Typography>
                </LocalizedStrict>
                <LocalizedEnum base="support-create-supplier-subscription-status-mode" enumValue={status}>
                    <StandardChip variant={getChipVariant(status)} className={classes.chip}>
                        Status
                    </StandardChip>
                </LocalizedEnum>
            </Stack>
            <Timeline className={classes.timeline}>
                {timelineItems.map((item, index) => (
                    <TimelineItem key={index}>
                        <TimelineSeparator>
                            <TimelineDot className={lastDotStyle(index)} />
                            {index < timelineItems.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                            <Stack flexDirection="row" justifyContent="space-between" gap="1rem">
                                <Typography variant="body2" className={classes.text}>
                                    {item.label}
                                </Typography>
                                <Typography variant="body2" textAlign="right" color={classes.accentText}>
                                    {item.date}
                                </Typography>
                            </Stack>
                        </TimelineContent>
                    </TimelineItem>
                ))}
            </Timeline>
            <Stack flexDirection="row" justifyContent="end">
                <LocalizedEnum base="support-create-supplier-subscription-status-button-mode" enumValue={status}>
                    <Button
                        onClick={() => {
                            setDialogState(true)
                        }}
                        color="primary"
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={getButtonIconVariant(status)} />}
                        size="small"
                        className={classes.button}
                    >
                        Status
                    </Button>
                </LocalizedEnum>
            </Stack>
            {dialogState && renderDialog()}
        </Stack>
    )
}
